// Safe environment variable access utilities
// This module provides safe access to environment variables without causing module loading errors

/**
 * Safely gets an environment variable value
 * Returns undefined if process is not available or if there's any error
 */
export function safeGetEnv(key: string): string | undefined {
  try {
    // Check if we're in a browser environment
    if (typeof window !== 'undefined') {
      // Check if process is available (it might not be in some browser environments)
      if (typeof process !== 'undefined' && process.env) {
        return process.env[key];
      }
    }
    return undefined;
  } catch (error) {
    // Silently fail and return undefined
    console.warn(`Failed to access environment variable ${key}:`, error);
    return undefined;
  }
}

/**
 * Gets an environment variable with a fallback value
 */
export function getEnvWithFallback(key: string, fallback: string): string {
  return safeGetEnv(key) || fallback;
}

/**
 * Checks if we can safely access environment variables
 */
export function canAccessEnv(): boolean {
  try {
    return typeof window !== 'undefined' && typeof process !== 'undefined' && !!process.env;
  } catch {
    return false;
  }
}

/**
 * Gets multiple environment variables at once
 */
export function getMultipleEnv(keys: string[]): Record<string, string | undefined> {
  const result: Record<string, string | undefined> = {};
  for (const key of keys) {
    result[key] = safeGetEnv(key);
  }
  return result;
}